// src/modules/calendar/components/AISchedulingChat.jsx
import React, { useState, useRef, useEffect } from 'react';
import { FiSend, FiX, FiZap, FiCheck, FiAlertTriangle, FiCalendar, FiClock, FiTrash2, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { chatWithCalendarAI } from '../../../services/gemini';

const QUICK_PROMPTS = [
    '¿Cuál es el estado actual de todos los cursos?',
    'Detecta cruces en los próximos 7 días',
    '¿Cuándo es la próxima masterclass de cada curso activo?',
    'Programa la siguiente masterclass de INCENDIO',
];

const formatTime = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('es-PE', { weekday: 'short', day: 'numeric', month: 'short' })
            + ' ' + d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
};

const EventProposalCard = ({ eventos, onAccept, onReject }) => {
    const [expanded, setExpanded] = useState(true);
    if (!eventos || eventos.length === 0) return null;
    return (
        <div className="mt-3 border border-blue-200 rounded-xl overflow-hidden bg-blue-50">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between px-3 py-2 bg-blue-100 hover:bg-blue-200 transition text-left"
            >
                <span className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
                    <FiCalendar size={12} /> {eventos.length} evento{eventos.length > 1 ? 's' : ''} propuesto{eventos.length > 1 ? 's' : ''}
                </span>
                {expanded ? <FiChevronUp size={13} className="text-blue-600" /> : <FiChevronDown size={13} className="text-blue-600" />}
            </button>
            {expanded && (
                <>
                    <div className="px-3 py-2 space-y-1.5 max-h-48 overflow-y-auto">
                        {eventos.map((ev, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                                <span
                                    className="w-2.5 h-2.5 rounded-full shrink-0"
                                    style={{ backgroundColor: ev.color || (ev.tipo === 'masterclass' ? '#8b5cf6' : '#3b82f6') }}
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-800 truncate">{ev.titulo}</p>
                                    <p className="text-gray-500 flex items-center gap-1">
                                        <FiClock size={10} /> {formatTime(ev.inicio)}
                                    </p>
                                </div>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ev.tipo === 'masterclass' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {ev.tipo === 'masterclass' ? 'MC' : 'Sesión'}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2 px-3 py-2 border-t border-blue-200">
                        <button
                            onClick={() => onAccept(eventos)}
                            className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition"
                        >
                            <FiCheck size={12} /> Crear todos
                        </button>
                        <button
                            onClick={onReject}
                            className="px-3 py-1.5 border border-gray-200 text-gray-500 text-xs rounded-lg hover:bg-gray-50 transition"
                        >
                            <FiTrash2 size={12} />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

const ConflictCard = ({ conflictos }) => {
    if (!conflictos || conflictos.length === 0) return null;
    return (
        <div className="mt-2 border border-orange-200 rounded-xl bg-orange-50 px-3 py-2">
            <p className="text-xs font-semibold text-orange-800 flex items-center gap-1 mb-1">
                <FiAlertTriangle size={12} /> {conflictos.length} conflicto{conflictos.length > 1 ? 's' : ''} detectado{conflictos.length > 1 ? 's' : ''}
            </p>
            {conflictos.map((c, i) => (
                <p key={i} className="text-xs text-orange-700">• {c}</p>
            ))}
        </div>
    );
};

const AISchedulingChat = ({ isOpen, onClose, events, instructors, courses, savedMemory, onCreateEvents }) => {
    const [messages, setMessages] = useState([
        {
            role: 'ai',
            content: '¡Hola! Soy tu asistente de programación. Puedo ayudarte a:\n• Programar masterclasses y sesiones\n• Detectar cruces de horarios\n• Ver el estado de cada curso\n• Planificar según disponibilidad de instructores\n\n¿Qué quieres programar hoy?',
            eventos: [],
            conflictos: [],
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [pendingEvents, setPendingEvents] = useState(null);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isLoading]);

    useEffect(() => {
        if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
    }, [isOpen]);

    const handleSend = async (text) => {
        const msg = (text || input).trim();
        if (!msg || isLoading) return;
        setInput('');

        const userMsg = { role: 'user', content: msg };
        setMessages(prev => [...prev, userMsg]);
        setIsLoading(true);

        try {
            const history = messages.map(m => ({ role: m.role, content: m.content }));
            const result = await chatWithCalendarAI(msg, history, { events, instructors, courses, savedMemory });

            setMessages(prev => [...prev, {
                role: 'ai',
                content: result.respuesta,
                eventos: result.eventos || [],
                conflictos: result.conflictos || [],
            }]);
        } catch (err) {
            setMessages(prev => [...prev, {
                role: 'ai',
                content: `Ocurrió un error: ${err.message}. Intenta de nuevo.`,
                eventos: [],
                conflictos: [],
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAcceptEvents = async (eventos) => {
        try {
            await onCreateEvents(eventos);
            setPendingEvents(null);
            setMessages(prev => [...prev, {
                role: 'ai',
                content: `✅ ¡Listo! Creé ${eventos.length} evento${eventos.length > 1 ? 's' : ''} en el calendario.`,
                eventos: [],
                conflictos: [],
            }]);
        } catch (err) {
            toast.error('Error al crear eventos: ' + err.message);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed right-0 top-0 h-full w-[400px] bg-white shadow-2xl flex flex-col z-40 border-l border-gray-200"
            style={{ animation: 'slideInRight 0.2s ease-out' }}>

            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                        <FiZap size={16} className="text-white" />
                    </div>
                    <div>
                        <p className="text-white font-semibold text-sm">Asistente de Programación</p>
                        <p className="text-indigo-200 text-xs">
                            {savedMemory ? `Memoria: ${savedMemory.fechaAnalisis} · ${(savedMemory.cursos || []).length} cursos` : 'Sin memoria — analiza el calendario primero'}
                        </p>
                    </div>
                </div>
                <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 p-1.5 rounded-lg transition">
                    <FiX size={18} />
                </button>
            </div>

            {/* Quick prompts */}
            <div className="px-3 py-2 border-b border-gray-100 flex gap-2 overflow-x-auto shrink-0 scrollbar-hide">
                {QUICK_PROMPTS.map((p, i) => (
                    <button
                        key={i}
                        onClick={() => handleSend(p)}
                        disabled={isLoading}
                        className="shrink-0 text-xs px-2.5 py-1.5 bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100 transition border border-indigo-100 whitespace-nowrap disabled:opacity-50"
                    >
                        {p}
                    </button>
                ))}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
                {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[88%] ${msg.role === 'user' ? '' : ''}`}>
                            {msg.role === 'ai' && (
                                <div className="flex items-center gap-1.5 mb-1">
                                    <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                                        <FiZap size={10} className="text-white" />
                                    </div>
                                    <span className="text-xs text-gray-400 font-medium">Asistente IA</span>
                                </div>
                            )}
                            <div className={`px-3 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                                msg.role === 'user'
                                    ? 'bg-indigo-600 text-white rounded-br-sm'
                                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                            }`}>
                                {msg.content}
                            </div>
                            {msg.role === 'ai' && msg.conflictos?.length > 0 && (
                                <ConflictCard conflictos={msg.conflictos} />
                            )}
                            {msg.role === 'ai' && msg.eventos?.length > 0 && (
                                <EventProposalCard
                                    eventos={msg.eventos}
                                    onAccept={handleAcceptEvents}
                                    onReject={() => setMessages(prev => prev.map((m, idx) =>
                                        idx === i ? { ...m, eventos: [] } : m
                                    ))}
                                />
                            )}
                        </div>
                    </div>
                ))}

                {/* Typing indicator */}
                {isLoading && (
                    <div className="flex justify-start">
                        <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                                <FiZap size={10} className="text-white" />
                            </div>
                            <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1">
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                        </div>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="px-3 py-3 border-t border-gray-100 shrink-0">
                <div className="flex gap-2 items-end">
                    <textarea
                        ref={inputRef}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder="Ej: Programa la siguiente masterclass de INCENDIO para el miércoles a las 7pm con Angel..."
                        rows={2}
                        disabled={isLoading}
                        className="flex-1 resize-none text-sm px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-gray-50 disabled:opacity-60"
                        style={{ maxHeight: '100px' }}
                    />
                    <button
                        onClick={() => handleSend()}
                        disabled={!input.trim() || isLoading}
                        className="p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                    >
                        <FiSend size={16} />
                    </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5 text-center">Enter para enviar · Shift+Enter nueva línea</p>
            </div>

            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
};

export default AISchedulingChat;
